import type { FC, ReactNode } from 'react'

import { ArrowRight, ArrowUpRight, FlaskConical } from 'lucide-react'
import { SectionHeading } from '@/components/generals/section-heading'
import { RESEARCH, external } from '@/constants/leesritme'

export const ResearchSection: FC = (): ReactNode => {
    return (
        <section id='onderzoek' className='bg-leaf-soft/60 py-20 sm:py-28'>
            <div className='mx-auto max-w-7xl px-5 sm:px-8'>
                <div className='flex flex-col gap-6 lg:flex-row lg:items-end lg:justify-between'>
                    <SectionHeading
                        eyebrow='Waarom het werkt'
                        title={(
                            <>
                                Gebouwd op wat onderzoek <span className='font-medium text-leaf italic'>laat zien.</span>
                            </>
                        )}
                        intro='Elk onderdeel van Leesritme komt uit onderzoek naar wat lezers sterker maakt.'
                    />

                    <a
                        href={external('/verantwoording')}
                        className='inline-flex shrink-0 items-center gap-1.5 self-start font-bold text-leaf underline decoration-leaf/30 underline-offset-4 hover:decoration-leaf lg:self-auto'
                    >
                        Lees de didactische verantwoording
                        <ArrowUpRight className='size-4' aria-hidden='true' />
                    </a>
                </div>

                <ol className='mt-12 grid gap-5 md:grid-cols-2'>
                    {RESEARCH.map((item, index) => (
                        <li key={item.title} className='reveal flex flex-col rounded-[1.75rem] bg-card p-7 shadow-soft sm:p-8'>
                            <span className='font-heading text-sm font-semibold text-leaf tabular-nums'>
                                0{index + 1}
                            </span>

                            <h3 className='mt-3 font-heading text-2xl leading-tight font-semibold text-ink'>
                                {item.title}
                            </h3>

                            <p className='mt-3 text-[1.0625rem] leading-relaxed text-muted-foreground'>
                                {item.text}
                            </p>

                            <p className='mt-5 flex items-start gap-2 rounded-xl bg-sun-soft px-4 py-3 text-[0.9375rem] text-ink'>
                                <ArrowRight className='mt-0.5 size-4 shrink-0 text-coral-deep' aria-hidden='true' />
                                <span>
                                    <span className='font-bold'>In Leesritme: </span>
                                    {item.inApp}
                                </span>
                            </p>

                            <p className='mt-auto flex flex-wrap gap-2 pt-5'>
                                {item.sources.map(source => (
                                    <a
                                        key={source.href}
                                        href={source.href}
                                        target='_blank'
                                        rel='noopener noreferrer'
                                        className='inline-flex items-center gap-1 rounded-full border border-ink/15 px-3 py-1 text-xs font-semibold text-ink/80 transition hover:border-ink/30 hover:bg-sand'
                                    >
                                        {source.label}
                                        <ArrowUpRight className='size-3' aria-hidden='true' />
                                    </a>
                                ))}
                            </p>
                        </li>
                    ))}
                </ol>

                <div className='reveal mt-6 flex flex-col gap-4 rounded-[1.75rem] border-2 border-dashed border-leaf/30 bg-white/60 p-6 sm:flex-row sm:items-center sm:p-8'>
                    <span className='grid size-12 shrink-0 place-items-center rounded-2xl bg-leaf text-white'>
                        <FlaskConical className='size-6' aria-hidden='true' />
                    </span>

                    <p className='text-[1.0625rem] leading-relaxed text-ink'>
                        <span className='font-bold'>Eerlijk is eerlijk: de onderdelen zijn onderzocht, Leesritme zelf nog niet.</span>{' '}
                        Daarom hoort bij de pilot op elke school een meting vooraf en achteraf. Zo zien we samen of het werkt.
                    </p>
                </div>
            </div>
        </section>
    )
}
